import { AnswerViewBlock } from '@/components/view/AnswerViewBlock';
import { AttachFileViewDTO } from '@/types/attachment/AttachFileViewDTO';
import { AttachmentStatus } from '@/types/attachment/AttachmentStatus';
import { Box, Divider, Grid, Skeleton, Stack, Typography, useMediaQuery } from '@mui/material';
import { EnclosureViewDTO } from '@/types/enclosure/EnclosureViewDTO';
import { ImageView, ImageViewItem, useImageView } from '@/app/hooks/useImageView';
import { ImageViewDialog } from '@/components/view/image/ImageViewDialog';
import { LetterHistory } from '@/components/letter/history/LetterHistory';
import { LetterView } from '@/components/letter/LetterView';
import { MobileLetterHistory } from '@/components/letter/history/MobileLetterHistory';
import { PageHeader } from '@/components/common/PageHeader';
import { ROUTE_OUTBOX } from '@/app/routes';
import { Theme } from '@mui/material/styles';
import { VerdictDot } from '@/components/letter/VerdictDot';
import { formatPhotoLabel } from '@/utils/string-utils';
import { formatShortDateTimeString } from '@/utils/date-utils';
import { useGetAnswerQuery } from '@/services/api/answerApiSlice';
import { useGetEventLogQuery } from '@/services/api/eventApiSlice';
import { useGetLetterQuery } from '@/services/api/letterApiSlice';
import { useGetPhotoMutation } from '@/services/api/attachFileApiSlice';
import { useParams } from 'react-router-dom';
import React, { FC, Fragment, ReactElement } from 'react';

type Params = {
  id: string;
};

export const OutboxLetterPage: FC = (): ReactElement => {
  const { id } = useParams<Params>();

  const letterId = Number(id);

  const { data: letter, isFetching, isLoading } = useGetLetterQuery(letterId);
  const { data: answer } = useGetAnswerQuery(letterId);
  const { data: events, isLoading: isEventLogLoading, isFetching: isEventLogFetching } = useGetEventLogQuery(letterId);

  const [getPhoto] = useGetPhotoMutation();

  const imageView: ImageView = useImageView();

  /**
   * Opens one photo full screen. The list draws stored previews, so the original is read here and
   * only for the photo the sender actually asked to see.
   *
   * @param photos The photos of the letter, in the order the page draws them.
   * @param index The position within that list of the photo the sender clicked.
   */
  const handleAttachFileClick = async (photos: AttachFileViewDTO[], index: number): Promise<void> => {
    await imageView.openList(
      photos.map(
        (attachFile: AttachFileViewDTO): ImageViewItem => ({
          name: formatPhotoLabel(attachFile.photoNumber, attachFile.photoIndex, photos.length),
          declined: attachFile.status === AttachmentStatus.DECLINED,
          read: async (): Promise<string | undefined> => {
            const photo: AttachFileViewDTO = await getPhoto(attachFile.id).unwrap();
            return photo?.content;
          },
        }),
      ),
      index,
    );
  };

  const loading: boolean = isFetching || isLoading;
  const eventLogLoading: boolean = isEventLogLoading || isEventLogFetching;

  const isMobile: boolean = useMediaQuery((theme: Theme): string => theme.breakpoints.down('sm'));

  return (
    <Fragment>
      <Grid container={true} direction={'column'} wrap={'nowrap'} spacing={5}>
        <Grid item={true}>
          <Grid container={true} direction={'row'} spacing={7}>
            <Grid item={true} xs={12} sm={9}>
              <Grid container={true} direction={'column'} spacing={isMobile ? 3 : 4}>
                <Grid item={true}>
                  <Grid
                    container={true}
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    spacing={2}>
                    <Grid item={true} xs={true}>
                      <PageHeader
                        previousFormPath={ROUTE_OUTBOX}
                        previousFormLabel={'Исходящие'}
                        tittle={
                          <Stack direction={'row'} spacing={1}>
                            <span>{'Письмо №'}</span>
                            <span>{loading ? <Skeleton width={'100px'} height={'32px'} /> : letter?.number}</span>
                          </Stack>
                        }
                        isMobile={isMobile}
                        mobileTittle={
                          <Stack direction={'row'} spacing={1}>
                            <span>{'Письмо №'}</span>
                            <span>{loading ? <Skeleton width={'100px'} height={'32px'} /> : letter?.number}</span>
                          </Stack>
                        }
                      />
                    </Grid>
                    <Grid item={true} mt={isMobile ? 1 : 0}>
                      <Typography variant={'label1'} sx={{ color: (theme: Theme) => theme.colors.grayText }}>
                        {loading ? (
                          <Skeleton width={'90px'} height={'16px'} />
                        ) : letter ? (
                          formatShortDateTimeString(letter.sentDate)
                        ) : (
                          ''
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                {isMobile && (
                  <Grid item={true}>
                    <MobileLetterHistory events={events} isLoading={eventLogLoading} />
                  </Grid>
                )}
                <Grid item={true}>
                  <Grid container={true} direction={'column'} spacing={isMobile ? 4 : 5}>
                    <Grid item={true}>
                      <Grid container={true} direction={'column'} spacing={4}>
                        <Grid item={true}>
                          <LetterView
                            letter={letter}
                            isLoading={loading}
                            onAttachFileClick={handleAttachFileClick}
                            onEnclosureClick={(enclosure: EnclosureViewDTO, name: string): void => {
                              imageView.show(name, enclosure?.content);
                            }}
                          />
                        </Grid>
                        {answer && (
                          <Fragment>
                            <Grid item={true}>
                              <Divider />
                            </Grid>
                            <Grid item={true}>
                              <AnswerViewBlock answer={answer} />
                            </Grid>
                          </Fragment>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {!isMobile && (
              <Grid item={true} xs={3}>
                <LetterHistory events={events} isLoading={eventLogLoading} />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <ImageViewDialog
        open={imageView.isOpen}
        isLoading={imageView.isLoading}
        isFailed={imageView.isFailed}
        content={imageView.content || ''}
        onPreviousImage={imageView.hasPrevious ? imageView.previous : undefined}
        onNextImage={imageView.hasNext ? imageView.next : undefined}
        title={
          <Grid container={true} direction={'row'} alignItems={'center'} spacing={2}>
            <Grid item={true}>
              <Typography variant={'h1'} fontSize={'24px'} lineHeight={'20px'}>
                {`Вложение письма № ${letter?.number}`}
              </Typography>
            </Grid>
            <Grid item={true}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {imageView.declined !== undefined && <VerdictDot declined={imageView.declined} />}
                <Typography
                  variant={'label2'}
                  fontSize={'12px'}
                  lineHeight={'20px'}
                  sx={{ color: (theme: Theme) => theme.colors.grayText }}>
                  {imageView.name}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        }
        onClose={imageView.close}
      />
    </Fragment>
  );
};
